'use client'

import { useEffect } from 'react'
import { IconBookmark, IconVolume } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { CharacterDrawingTab } from '@/components/details/character-drawing-tab'
import { SaveStudyWordButton } from '@/components/study/save-study-word-button'
import { WordFamiliarityBadge } from '@/components/study/word-familiarity-badge'
import { useAudio } from '@/hooks/useAudio'
import { analytics } from '@/lib/analytics'
import type { StudyWordEntry } from '@/lib/save-to-study'
import type { SearchResult } from '@/types/dictionary'

export function PublicEntryViewed({
  entryId,
  word
}: {
  entryId: number
  word: string
}) {
  useEffect(() => {
    analytics.publicDictionaryEntryViewed(entryId, word)
  }, [entryId, word])

  return null
}

export function PublicEntryAudioButton({
  word,
  entryId
}: {
  word: string
  entryId: number
}) {
  const { play, isPlaying } = useAudio('simplified')

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="h-11 w-11 rounded-full"
      aria-label={`Play audio for ${word}`}
      disabled={isPlaying}
      onClick={() => {
        analytics.wordAudioPlayed(entryId, word)
        play(word)
      }}
    >
      <IconVolume className="h-5 w-5" />
    </Button>
  )
}

export function PublicEntrySaveButton({
  entryId,
  entry,
  word,
  label = 'Save word'
}: {
  entryId: number
  entry: StudyWordEntry
  word: string
  label?: string
}) {
  return (
    <div
      onClickCapture={() => analytics.publicWordSaveClicked(entryId, word)}
      className="flex w-full flex-col items-center gap-2 sm:w-auto"
    >
      <SaveStudyWordButton
        entry={entry}
        source={{ page: 'public-dictionary-entry' }}
        label={label}
        variant="outline"
        className="h-10 w-full rounded-full px-4 sm:w-auto"
      />
      <WordFamiliarityBadge entryId={entryId} />
    </div>
  )
}

export function PublicEntryStrokePractice({ entry }: { entry: SearchResult }) {
  return (
    <CharacterDrawingTab
      entry={entry}
      primaryText={entry.simplified}
      secondaryText={
        entry.traditional !== entry.simplified ? entry.traditional : null
      }
      primaryScriptType="simplified"
      secondaryScriptType="traditional"
    />
  )
}

export function PublicEntryStudyIcon() {
  return <IconBookmark className="h-4 w-4" />
}
